import type { LeadEditPatch, PersonEditInput } from '@viq-admin/screens';
import type { LeadCompleteness, LeadField } from '@viq-admin/types';
import { adminFetch } from './dataProvider';
import { editLead, fetchLeadDetail } from './leadEdit';

/**
 * CRM merge-engine calls for the p3 Clean-Match-Send flow (ADR 019 / p2
 * spec §5-§7). `source` is a bare string, not the narrower `CrmSource` type
 * from `@viq-admin/types` (currently `'salesforce'` only) — the capabilities
 * route already reports `salesforce`/`hubspot`/`zoho`, and match-check/
 * preview/commit/owners are source-parameterized routes the backend 404s on
 * for a source it doesn't support yet, so the client stays forward-
 * compatible rather than hard-coding today's single supported source.
 *
 * Wire types below mirror viq-backend `crm/p2-merge-engine-and-endpoints`
 * (`shared/src/crm/engine/{matchCheck,preview,commit,diff}.ts`,
 * `shared/src/crm/types.ts`, `shared/src/types/crm.ts`) field-for-field.
 * They are NOT imported from a shared package — this SPA has no dependency
 * on viq-backend — so any drift is caught by hand at review time, same as
 * the rest of `api/types.ts`.
 *
 * SECOND MIRROR — keep in step: `packages/screens/src/batch/crm/types.ts`
 * mirrors this same wire contract independently (`packages/*` cannot import
 * `apps/*`, so it can't just import these types). `BatchRoute.tsx` wires
 * this module's fetch functions (`crmPreview`, `crmCommit`, ...) into that
 * package's `CrmQueueApi` interface, so TypeScript structurally checks one
 * side against the other at that call site — a field added here and not
 * there (or vice versa) either fails that build (if required) or silently
 * vanishes on the stale side (if optional) without either module ever
 * importing the other. Add a field to both, same spelling and shape.
 */

// ── Match-check ──────────────────────────────────────────────────────────

export type MatchBucket = 'new' | 'existing' | 'needs_email' | 'ambiguous';

/**
 * Where a row's `suggestedOwnerId` came from — the owner fallback chain
 * (spec 1 §3). Mirrors `OwnerProvenance` in
 * `packages/screens/src/batch/crm/types.ts` (kept in step, see the header
 * comment above).
 */
export type OwnerProvenance =
  | 'assigned_to'
  | 'suggested'
  | 'conference_default'
  | 'company_default'
  | 'integration_account';

export interface PersonMatchState {
  personId: string;
  displayName: string;
  bucket: MatchBucket;
  email: string | null;
  remoteType: 'lead' | 'contact' | null;
  remoteId: string | null;
  /** Informational, fresh checks only. */
  similar: string | null;
  fromCache: boolean;
}

export interface MatchCheckResponse {
  results: PersonMatchState[];
  counts: { new: number; existing: number; needsEmail: number; ambiguous: number };
}

// ── Preview ──────────────────────────────────────────────────────────────

export interface FieldDecision {
  field: string;
  ours: string | null;
  theirs: string | null;
  action: 'fill' | 'overwrite' | 'keep_crm' | 'noop';
  reason:
    | 'theirs_blank'
    | 'conflict_default'
    | 'conflict_choice'
    | 'policy_overwrite'
    | 'equal'
    | 'ours_blank'
    | 'both_blank'
    | 'not_patchable';
}

/**
 * Drift token echoed back to commit. `bucket` travels with it so the
 * backend can tell "previewed as ambiguous" apart from "previewed as a
 * confident single hit" — the commit Zod schema (`CrmCommitInputSchema`)
 * REQUIRES it, so every `expected` in this module carries it too.
 */
export interface PersonPreview {
  personId: string;
  displayName: string;
  bucket: MatchBucket;
  /**
   * False for a person with no usable email — they never went through the
   * CRM email walk (still fully sendable; matched instead by the VendoIQ id
   * property). True for every row that did go through the email walk.
   * Optional deploy-skew field: absent on an older backend's response —
   * treat absence as unknown, not as `true`.
   */
  emailChecked?: boolean;
  remoteType: 'lead' | 'contact' | null;
  remoteId: string | null;
  /** [] unless bucket === 'existing'. */
  decisions: FieldDecision[];
  currentOwner: { id: string; name: string } | null;
  suggestedOwnerId: string | null;
  /** Where `suggestedOwnerId` came from (or why it's null). Optional deploy-skew field. */
  ownerProvenance?: OwnerProvenance;
  /**
   * True only for a configured conference/company default owner id that
   * could not be confirmed against the CRM's live, assignable user list.
   * Optional deploy-skew field; absence must not be read as `false`.
   */
  ownerUnverified?: boolean;
  ownerRequired: boolean;
  expected: { remoteId: string | null; lastModified: string | null; bucket: MatchBucket };
  similar: string | null;
  /** Booth conversations + open tasks the send will log. Absent = nothing. */
  activitiesToPush?: { calls: number; tasks: number };
  /** Company lead rules for this lead (spec 2026-09-14 §2.2); see the screens copy. */
  completeness?: LeadCompleteness | null;
}

export interface PreviewSummary {
  toCreate: number;
  toUpdate: number;
  membershipOnly: number;
  needsEmail: number;
  ambiguous: number;
  needsOwner: number;
  suggestedOwners: number;
  /**
   * Rows whose owner fallback chain reached the last step (`ownerProvenance
   * === 'integration_account'`). Optional deploy-skew field.
   */
  integrationAccountOwned?: number;
  /** Rows whose `ownerUnverified` row flag is true. Optional deploy-skew field. */
  ownerUnverified?: number;
  activitiesToPush: number;
  /**
   * How many of the requested people a `scope: 'changed'` request excluded
   * as already sent with nothing new since. Optional deploy-skew field.
   */
  collapsed?: number;
  /**
   * True when this request's CRM owners lookup failed and degraded to an
   * empty list — every row's owner resolution is then unreliable. Optional
   * deploy-skew field.
   */
  ownersUnavailable?: boolean;
}

export type MergePolicy = 'fillBlanksOnly' | 'overwrite';

export interface PreviewResponse {
  previews: PersonPreview[];
  summary: PreviewSummary;
  policy: MergePolicy;
  /**
   * `false` when the company's Salesforce settings forbid overwriting CRM
   * values (spec 2026-09-28 §5.8). Mirrors `PreviewResponse.overwriteAllowed`
   * in `packages/screens/src/batch/crm/types.ts` (kept in step, see the
   * header comment above). Absent means allowed.
   */
  overwriteAllowed?: boolean;
}

/**
 * Preview input scope (spec 4 §3). Mirrors `PreviewScope` in
 * `packages/screens/src/batch/crm/types.ts` (kept in step, see the header
 * comment above). Absent on the wire means `'all'` — this type only names
 * the two EXPLICIT values `crmPreview` may send.
 */
export type PreviewScope = 'changed' | 'all';

// ── Commit ───────────────────────────────────────────────────────────────

export type FieldChoice = 'ours' | 'theirs';

export interface CrmCommitBody {
  /** Per-run override of the tenant default merge policy. */
  policy?: MergePolicy;
  persons: Array<{
    personId: string;
    ownerId?: string;
    fieldChoices?: Record<string, FieldChoice>;
    expected: { remoteId: string | null; lastModified: string | null; bucket: MatchBucket };
  }>;
}

export interface CommitResultRow {
  personId: string;
  outcome:
    | 'created'
    | 'updated'
    | 'membership_only'
    | 'changed_since_preview'
    | 'needs_email'
    | 'ambiguous'
    | 'failed'
    // Company lead rules held this lead back in `block` mode — notes below the
    // company minimum (viq-vault spec 2026-09-14 §2.3) or a missing field
    // VendoIQ staff hold (spec 2026-09-28 §6). Produced by the backend commit
    // handler, never by the merge engine.
    | 'incomplete';
  remoteType: 'lead' | 'contact' | null;
  remoteId: string | null;
  /**
   * Set on every `failed` row, and on the one `changed_since_preview` row
   * that needs to explain itself. Otherwise `null`.
   */
  reason: string | null;
  /** Per-person activity outcome, present only where there was work to do. */
  activities?: { logged: number; failed: number };
  /**
   * Opportunity mode only (spec 2026-09-28 §5.9): how the record was tied to
   * the event. Mirrors `CommitResultRow.eventLink` in
   * `packages/screens/src/batch/crm/types.ts` (kept in step, see the header
   * comment above).
   */
  eventLink?: 'linked' | 'kept_existing' | 'contact_role_added' | 'not_linked';
  /**
   * Under a company's scanner rule for Tasks (spec 2026-09-28 D9): which
   * owner-chain step owned this person's Tasks. Mirrors
   * `CommitResultRow.taskOwner` in `packages/screens/src/batch/crm/types.ts`.
   */
  taskOwner?: OwnerProvenance;
  /**
   * `incomplete` rows held back for missing held fields (spec 2026-09-28 §6).
   * Mirrors `CommitResultRow.heldFields` in
   * `packages/screens/src/batch/crm/types.ts` (kept in step, see the header
   * comment above).
   */
  heldFields?: LeadField[];
}

export interface CommitOutcome {
  results: CommitResultRow[];
  summary: {
    created: number;
    updated: number;
    membershipOnly: number;
    changedSincePreview: number;
    ambiguous: number;
    failed: number;
    containerId: string;
    activitiesLogged: number;
    activitiesFailed: number;
    /** Leads held back by company lead rules; absent from older backends and when none were. */
    incomplete?: number;
  };
}

// ── Owners ───────────────────────────────────────────────────────────────

export interface CrmOwner {
  id: string;
  name: string;
  email: string;
  role: string;
}

// ── Fetchers ─────────────────────────────────────────────────────────────

export const crmMatchCheck = (
  conferenceId: string,
  source: string,
  body: { personIds?: string[]; force?: boolean } = {},
) =>
  adminFetch<MatchCheckResponse>(`/v1/admin/conferences/${conferenceId}/crm/${source}/match-check`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const crmPreview = (
  conferenceId: string,
  source: string,
  personIds: string[],
  scope?: PreviewScope,
) =>
  adminFetch<PreviewResponse>(`/v1/admin/conferences/${conferenceId}/crm/${source}/preview`, {
    method: 'POST',
    // `scope` reaches the body only when the caller passed one — an
    // undefined `scope` key would serialize away under `JSON.stringify`
    // regardless, but the spread keeps the omission explicit and matches the
    // backend's own "absent ⇒ 'all'" contract instead of relying on that
    // implicit behavior.
    body: JSON.stringify({ personIds, ...(scope ? { scope } : {}) }),
  });

export const crmCommit = (conferenceId: string, source: string, body: CrmCommitBody) =>
  adminFetch<CommitOutcome>(`/v1/admin/conferences/${conferenceId}/crm/${source}/commit`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const crmOwners = (source: string) =>
  adminFetch<{ owners: CrmOwner[] }>(`/v1/admin/crm/${source}/owners`).then((r) => r.owners);

// ── Lead editing (composed over the existing lead-update call) ─────────────

/**
 * Lead editing for the merge/match UI (e.g. fixing a `needs_email` person's
 * email inline before a match-check re-run). There is no partial-patch
 * lead-update endpoint on the backend — `PATCH /v1/admin/leads/:id`'s Zod
 * schema (`PersonEditSchema`, viq-backend `shared/src/types/person.ts`)
 * requires ALL ten tear-sheet fields plus `expectedVersion` (optimistic
 * lock, 409 on drift) — so this composes the two calls that already exist
 * instead of re-implementing either:
 *
 *  1. `fetchLeadDetail` (`GET /v1/admin/leads/:id`, `./leadEdit.ts`) for a
 *     FRESH `person` + `serverVersion` — never a stale caller-held version,
 *     which would spuriously 409 or (worse) silently win a stale write.
 *  2. Merge `patch` over the fetched editable fields.
 *  3. `editLead` (`PATCH /v1/admin/leads/:id`, shipped 2026-07-26) with the
 *     merged fields and the just-fetched `serverVersion`.
 *
 * A version conflict (409) or any other failure from either call propagates
 * as a rejected promise — no retry loop here; the caller surfaces the error.
 */
export async function updateLeadFields(leadId: string, patch: LeadEditPatch): Promise<void> {
  const { person } = await fetchLeadDetail(leadId);
  const fields: PersonEditInput = {
    firstName: person.firstName ?? '',
    lastName: person.lastName ?? '',
    displayName: person.displayName,
    title: person.title,
    company: person.company,
    email: person.email,
    phone: person.phone,
    linkedinUrl: person.linkedinUrl,
    status: person.status,
    temperature: person.temperature,
    ...patch,
  };
  await editLead(leadId, fields, person.serverVersion ?? 0);
}
